// FilePane.js
// The file itself, inline on the left of the file review dialog (Spec 2 section
// 6.4), so the person can see what Decode read while they review what it decided.
//
//   A workbook: one tab per sheet (a sheet whose tables are all ignored is
//   dimmed) over the sheet copy, read-only, with the sheet plan's tables painted
//   over it (SheetViewer, which the sheet workspace draws with too). In the unit
//   view the unit's own sheet is shown and its table lifted; the rest of the
//   sheet is washed grey.
//
//   A document: its pages (DocumentPagePane), with the unit's page range lifted.
//
// A gutter on the pane's right edge drags its width, between MIN_WIDTH and
// MAX_SHARE of the dialog; a button collapses it to a narrow rail.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { activeDecisions, activeValue } from '../../../utils/decisions';
import { countNoun } from '../../../utils/pluralise';
import SheetViewer from './SheetViewer';
import DocumentPagePane from './DocumentPagePane';

const DEFAULT_WIDTH = 520;
const MIN_WIDTH = 260;
const MAX_SHARE = 0.7;
const RAIL_WIDTH = 44;

function FilePane({ companyId, tableId, file, units, focusUnit = null }) {
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [collapsed, setCollapsed] = useState(false);
  const [chosenSheet, setChosenSheet] = useState(null);
  const paneRef = useRef(null);
  // The listeners of a drag in progress, removed on release or when the pane goes.
  const stopDragRef = useRef(null);
  useEffect(() => () => stopDragRef.current?.(), []);

  const startDrag = (event) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = width;
    const dialogWidth = paneRef.current.parentElement.getBoundingClientRect().width;
    const onMove = (moveEvent) => {
      const next = startWidth + moveEvent.clientX - startX;
      setWidth(Math.max(MIN_WIDTH, Math.min(dialogWidth * MAX_SHARE, next)));
    };
    const stop = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', stop);
      stopDragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', stop);
    stopDragRef.current = stop;
  };

  // ── What to show ──────────────────────────────────────────────────────────
  const isDocument = file.type === 'pdf';
  const segmentRecord = file.processes.segment;
  // The sheets in the order the file chain read them, which is the workbook's.
  const sheetNames = segmentRecord ? activeDecisions(segmentRecord).map(({ key }) => key) : [];
  // In the unit view the unit's own sheet is shown, whichever tab was chosen before.
  const focusSheet = focusUnit?.source?.sheet || null;
  const shownSheet = focusSheet
    || (sheetNames.includes(chosenSheet) ? chosenSheet : null)
    || sheetNames[0]
    || null;
  // The file and its units arrive by separate listeners, so for a moment a unit
  // can name a sheet whose plan this copy of the file does not hold yet.
  const regions = useMemo(() => {
    const plan = shownSheet && segmentRecord ? activeValue(segmentRecord, shownSheet) : null;
    return plan ? plan.regions : [];
  }, [segmentRecord, shownSheet]);
  const ignoredRegionIds = useMemo(() => units
    .filter((unit) => unit.source.sheet === shownSheet && unit.source.region_id && unit.disposition === 'ignore')
    .map((unit) => unit.source.region_id), [units, shownSheet]);
  let focus = null;
  if (focusUnit?.source?.region_id) focus = focusUnit.source.region_id;
  else if (focusUnit?.source?.fields) focus = 'fields';

  const sheetIsIgnored = (sheetName) => {
    const onSheet = units.filter((unit) => unit.source.sheet === sheetName);
    return onSheet.length > 0 && onSheet.every((unit) => unit.disposition === 'ignore');
  };

  if (collapsed) {
    return (
      <Box
        ref={paneRef}
        sx={{ width: RAIL_WIDTH, flex: 'none', borderRight: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center', pt: 1 }}
      >
        <Button
          size="small"
          onClick={() => setCollapsed(false)}
          sx={{ writingMode: 'vertical-rl', minWidth: 0, textTransform: 'none' }}
        >
          Show the file
        </Button>
      </Box>
    );
  }

  let head;
  let body;
  if (isDocument) {
    const focusRange = focusUnit?.source?.pages || null;
    head = (
      <Typography variant="caption" color="text.secondary" sx={{ px: 1, flex: 1 }}>
        {file.assets.page_count ? countNoun(file.assets.page_count, 'page') : 'No pages yet'}
        {focusRange ? ` · ${focusUnit.name} is on pages ${focusRange[0]}–${focusRange[1]}` : ''}
      </Typography>
    );
    body = file.assets.pages
      ? (
        <Box sx={{ p: 1 }}>
          <DocumentPagePane
            companyId={companyId}
            tableId={tableId}
            fileId={file.id}
            pages={file.assets.pages}
            pageCount={file.assets.page_count}
            focusRange={focusRange}
          />
        </Box>
      )
      : <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>The file is still being read.</Typography>;
  } else {
    head = (
      <Tabs
        value={shownSheet || false}
        onChange={(event, sheetName) => setChosenSheet(sheetName)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ flex: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none' } }}
      >
        {sheetNames.map((sheetName) => (
          <Tab
            key={sheetName}
            value={sheetName}
            label={sheetName}
            title={sheetIsIgnored(sheetName) ? 'Every table on this sheet is ignored' : undefined}
            sx={{ opacity: sheetIsIgnored(sheetName) ? 0.5 : 1 }}
          />
        ))}
      </Tabs>
    );
    const sheetCopyPath = shownSheet ? file.assets.sheet_copies?.[shownSheet] : null;
    body = sheetCopyPath
      ? (
        <Box sx={{ height: '100%', p: 1, display: 'flex', flexDirection: 'column' }}>
          <SheetViewer
            companyId={companyId}
            tableId={tableId}
            fileId={file.id}
            sheetCopyPath={sheetCopyPath}
            sheetName={shownSheet}
            regions={regions}
            ignoredRegionIds={ignoredRegionIds}
            focus={focus}
          />
        </Box>
      )
      : <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>The file is still being read.</Typography>;
  }

  return (
    <>
      <Box
        ref={paneRef}
        data-testid="file-pane"
        sx={{ width, flex: 'none', display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider', minHeight: 40 }}>
          {head}
          <Tooltip title="Hide the file">
            <IconButton size="small" aria-label="Hide the file" onClick={() => setCollapsed(true)}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: isDocument ? 'auto' : 'hidden' }}>{body}</Box>
      </Box>
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Drag to resize the file"
        onMouseDown={startDrag}
        sx={{
          width: 6,
          flex: 'none',
          cursor: 'col-resize',
          bgcolor: 'divider',
          '&:hover': { bgcolor: 'primary.light' },
        }}
      />
    </>
  );
}

export default FilePane;
